import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { Button, Skeleton, Typography } from "@mui/material";
import { CHAT_CONTENT_MAX_WIDTH } from "@features/Chat/chat-helpers";
import { baseTheme, IconButton, useBreakpoint } from "@avenue-8/sidekick-ui";
import type {
  PromptLibraryCategory,
  PromptLibraryItem,
} from "@shared/apis/sidekick/generated";
import { ExpandSidebarButton } from "@shared/components/ExpandSidebarButton";
import { SvgIcon } from "@shared/components/SvgIcon";
import { useUIStore } from "@store/ui.store";
import { HeaderMoreActions } from "@features/Chat/components/AppNavbar/components/HeaderMoreActions";
import { CategoryCards } from "./components/CategoryCards";
import { ParticlesBackground } from "./components/ParticlesBackground";

const PanelRoot = styled.div`
  grid-area: suggestions;
  position: relative;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  background: ${baseTheme.colors.blue5};
`;

const HeaderBar = styled.div`
  width: 100%;
  padding: 0.75rem 0.5rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  background: transparent;
`;

const HeaderSide = styled.div<{ $align: "left" | "right" }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$align === "left" ? "flex-start" : "flex-end")};
`;

const Panel = styled.div`
  width: 100%;
  max-width: calc(${CHAT_CONTENT_MAX_WIDTH} + 2.5rem);
  margin: 0 auto;
  padding: 1rem 4rem 2rem 4rem;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;

  ${(p) => p.theme.media.laptopDown} {
    padding: 1rem 1.75rem 2rem 1.75rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const SubcategoryBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SubcategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(p) => p.theme.colors.white};

  svg {
    color: ${(p) => p.theme.colors.white};
  }
`;

const BackToNewChatButton = styled(IconButton)`
  color: ${baseTheme.colors.white};
  border-radius: 50%;
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
  &:active {
    background-color: rgba(255, 255, 255, 0.16);
  }
  &:focus-visible {
    background-color: rgba(255, 255, 255, 0.12);
  }
`;

const SkeletonCardsRow = styled.div`
  display: flex;
  gap: 1rem;
  overflow: hidden;
`;

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  color: ${baseTheme.colors.white};
  text-align: center;
`;

const SKELETON_COLOR = "rgba(255, 255, 255, 0.12)";

const ExplorePromptsSkeleton = () => (
  <>
    {Array.from({ length: 3 }).map((_, i) => (
      <SubcategoryBlock key={i}>
        <SubcategoryTitleRow>
          <Skeleton
            variant="circular"
            width={20}
            height={20}
            sx={{ bgcolor: SKELETON_COLOR, flexShrink: 0 }}
          />
          <Skeleton
            variant="text"
            width={200}
            height={32}
            sx={{ bgcolor: SKELETON_COLOR }}
          />
        </SubcategoryTitleRow>
        <SkeletonCardsRow>
          <Skeleton
            variant="rounded"
            sx={{
              flex: 1,
              height: 180,
              bgcolor: SKELETON_COLOR,
            }}
          />
        </SkeletonCardsRow>
      </SubcategoryBlock>
    ))}
  </>
);

type ExploreSubcategoryRow = {
  key: string;
  icon: string;
  label: string;
  items: PromptLibraryItem[];
};

function flattenSubcategoriesForExplore(
  categories: PromptLibraryCategory[],
): ExploreSubcategoryRow[] {
  return categories.flatMap((category) =>
    category.subcategories.map((sub) => ({
      key: `${category.id}:${sub.id}`,
      icon: sub.icon || category.icon,
      label: sub.label,
      items: sub.items,
    })),
  );
}

type Props = {
  categories: PromptLibraryCategory[];
  isLoading: boolean;
  isError: boolean;
  disabled?: boolean;
  onRetry: () => void;
  onSelectItem: (item: PromptLibraryItem) => void;
  onBackToNewChat: () => void;
};

export const ExplorePromptsPanel = ({
  categories,
  isLoading,
  isError,
  disabled,
  onRetry,
  onSelectItem,
  onBackToNewChat,
}: Props) => {
  const isLaptopDown = useBreakpoint("laptop");
  const isSidebarOpen = useUIStore((s) => s.isSidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const subcategoryRows = flattenSubcategoriesForExplore(categories);

  return (
    <PanelRoot>
      <ParticlesBackground />
      <HeaderBar>
        <HeaderSide $align="left">
          {/* Desktop's sidebar is always reachable via its own collapsed rail —
              only mobile (fully-hidden drawer) still needs this trigger here. */}
          {isLaptopDown && (
            <ExpandSidebarButton
              onClick={() => toggleSidebar(true)}
              hide={isSidebarOpen}
              buttonBgColor={baseTheme.colors.white}
            />
          )}
        </HeaderSide>
        <BackToNewChatButton
          aria-label="Back to New Chat"
          onClick={onBackToNewChat}
        >
          <CloseIcon htmlColor={baseTheme.colors.white} />
        </BackToNewChatButton>
        <HeaderSide $align="right">
          <HeaderMoreActions
            theme="light"
            buttonBgColor={baseTheme.colors.white}
          />
        </HeaderSide>
      </HeaderBar>
      <Panel>
        <Header>
          <TravelExploreIcon
            fontSize="large"
            htmlColor={baseTheme.colors.white}
          />
          <Typography variant="h3" color={baseTheme.colors.white}>
            Explore
          </Typography>
        </Header>

        {isLoading && <ExplorePromptsSkeleton />}

        {isError && !isLoading && (
          <ErrorBox>
            <Typography variant="body2" color={baseTheme.colors.white}>
              Could not load prompts. Please try again.
            </Typography>
            <Button variant="outlined" size="small" onClick={onRetry}>
              Retry
            </Button>
          </ErrorBox>
        )}

        {!isLoading &&
          !isError &&
          subcategoryRows
            .filter((row) => row.items.length > 0)
            .map((row) => (
              <SubcategoryBlock key={row.key}>
                <SubcategoryTitleRow>
                  <SvgIcon name={row.icon} fontSize="small" />
                  <Typography variant="h6" sx={{ color: "inherit" }}>
                    {row.label}
                  </Typography>
                </SubcategoryTitleRow>
                <CategoryCards
                  items={row.items}
                  disabled={disabled}
                  onSelectItem={onSelectItem}
                />
              </SubcategoryBlock>
            ))}
      </Panel>
    </PanelRoot>
  );
};
